import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { ChevronLeft, ChevronRight } from "lucide-react";
import DriveFileMoveOutlinedIcon from "@mui/icons-material/DriveFileMoveOutlined";
import { ReviewVideoFullscreenPlayer } from "../../components/ReviewVideoFullscreenPlayer";
import "./LiveTrainingTableReplayReview.css";

function getPlaybackPortalRoot() {
  if (typeof document === "undefined") return null;
  const fs =
    document.fullscreenElement ||
    document.webkitFullscreenElement ||
    document.mozFullScreenElement ||
    null;
  if (fs?.classList?.contains("lt-page")) return fs;
  return document.body;
}

function clipLabel(clip, t) {
  const parts = [];
  if (clip?.sequence_order) {
    parts.push(
      `${t("liveTrainingSequence", "Sequence")} ${clip.sequence_order}`
    );
  }
  if (clip?.camera_position) {
    parts.push(
      t("cameraPosition", "Camera {{position}}", {
        position: clip.camera_position,
      })
    );
  }
  return parts.join(" · ");
}

/**
 * Plays the evaluation videos recorded on one table of a finalized exercise.
 * Several clips (sequences / cameras) are browsed with previous / next.
 * Moderators can open move/associate for the current clip.
 */
export function LiveTrainingTableEvaluationPlayback({
  tableNumber = null,
  clips = [],
  onClose,
  brandPrimary = "#0877BC",
  brandSecondary = "#46528b",
  t = (k, d) => d || k,
  canMoveVideo = false,
  onMoveVideo = null,
}) {
  const list = Array.isArray(clips) ? clips.filter((c) => c?.video_url) : [];
  const [index, setIndex] = useState(0);

  useEffect(() => {
    setIndex(0);
  }, [tableNumber, list.length]);

  const clip = list[Math.min(index, list.length - 1)] || null;
  if (!clip || typeof document === "undefined") return null;

  const tableTitle =
    tableNumber != null
      ? t("table_with_number", { number: tableNumber })
      : t("evaluationVideo", "Evaluation video");
  const detail = clipLabel(clip, t);
  const title = detail ? `${tableTitle} — ${detail}` : tableTitle;
  const portalRoot = getPlaybackPortalRoot() || document.body;
  const showMove =
    canMoveVideo &&
    typeof onMoveVideo === "function" &&
    Boolean(clip.message_id);
  const showToolbar = list.length > 1 || showMove;

  return (
    <>
      <ReviewVideoFullscreenPlayer
        key={clip.message_id || clip.video_url}
        url={clip.video_url}
        onClose={onClose}
        title={title}
        autoPlay
        messageId={clip.message_id || null}
        teId={clip.te_id || null}
        tableId={clip.table_id || null}
        initialViewSetup={clip.view_setup || null}
        sequenceOrder={clip.sequence_order || null}
        enablePose={false}
        isEvaluationVideo
        isKotMatch={Boolean(clip.is_kot)}
        kotMatchId={clip.kot_match_id || null}
        className="review-video-fullscreen-player--table-replay"
      />
      {showToolbar
        ? createPortal(
            <div
              className="lt-replay-review-actions"
              role="toolbar"
              aria-label={tableTitle}
              style={{
                "--lt-brand": brandPrimary,
                "--lt-brand-secondary": brandSecondary,
              }}
            >
              {list.length > 1 ? (
                <>
                  <button
                    type="button"
                    className="lt-replay-review-actions__btn"
                    disabled={index <= 0}
                    onClick={() => setIndex((i) => Math.max(0, i - 1))}
                    aria-label={t("previous", "Previous")}
                  >
                    <ChevronLeft size={18} strokeWidth={2.25} aria-hidden />
                  </button>
                  <span className="lt-replay-review-actions__queue">
                    {t("liveTrainingEvalVideoIndex", "{{index}} / {{total}}", {
                      index: index + 1,
                      total: list.length,
                    })}
                  </span>
                  <button
                    type="button"
                    className="lt-replay-review-actions__btn"
                    disabled={index >= list.length - 1}
                    onClick={() =>
                      setIndex((i) => Math.min(list.length - 1, i + 1))
                    }
                    aria-label={t("next", "Next")}
                  >
                    <ChevronRight size={18} strokeWidth={2.25} aria-hidden />
                  </button>
                </>
              ) : null}
              {showMove ? (
                <button
                  type="button"
                  className="lt-replay-review-actions__btn"
                  onClick={() => onMoveVideo(clip)}
                  aria-label={
                    t("moveVideoUpdateParams", "Update video parameters") ||
                    "Associate to table"
                  }
                  title={
                    t(
                      "liveTrainingAssociateVideosToTables",
                      "Associate videos to tables"
                    ) || t("moveVideoUpdateParams", "Update video parameters")
                  }
                >
                  <DriveFileMoveOutlinedIcon fontSize="small" aria-hidden />
                  <span>
                    {t("liveTrainingAssociateVideoShort", "Associate")}
                  </span>
                </button>
              ) : null}
            </div>,
            portalRoot
          )
        : null}
    </>
  );
}

export default LiveTrainingTableEvaluationPlayback;
